import * as React from "react";
import {Box, Button, Divider, MenuItem, Select, Typography} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddIcon from "@mui/icons-material/Add";
import {useTranslation} from "react-i18next";
import {ApprovedBranch, JobPosition} from "../../../hooks/useJobPositions";
import JobStatusChip from "./JobStatusChip";
import JobAssignmentList, {JobAssignmentListProps} from "./JobAssignmentList";

export interface JobPositionDetailsProps {
    position: JobPosition;
    branches: ApprovedBranch[];
    assignmentProps: JobAssignmentListProps;
    onEdit: (row: JobPosition) => void;
    onAssign: (branchId: string) => void;
    /** Optional position-level status actions (the desktop list has these in its row menu instead). */
    onApprove?: (id: string) => void;
    onReject?: (id: string) => void;
    onClose?: (id: string) => void;
    /** Desktop shows the side-card heading (title, status, name); mobile omits it inside the expanded card. */
    showHeader?: boolean;
    /** "all" (mobile) renders everything; desktop renders details and assignments in separate cards. */
    section?: "all" | "details" | "assignments";
}

/** Position details: branch distribution, assign-to-branch and the edit button. */
export default function JobPositionDetails({
    position,
    branches,
    assignmentProps,
    onEdit,
    onAssign,
    onApprove,
    onReject,
    onClose,
    showHeader = false,
    section = "all",
}: Readonly<JobPositionDetailsProps>): React.ReactElement {
    const {t} = useTranslation();
    const [assignBranch, setAssignBranch] = React.useState<string>("");
    const showDetails = section !== "assignments";
    const showAssignments = section !== "details";

    return (
        <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
            {showDetails && showHeader && (
                <>
                    <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1}}>
                        <Typography variant="subtitle1" fontWeight={700}>{t("hr.jobs.details.title")}</Typography>
                        <JobStatusChip row={position}/>
                    </Box>
                    <Typography variant="h5" fontWeight={700}>{position.name}</Typography>
                    <Divider/>
                </>
            )}

            {showAssignments && (
                <>
                    <Box>
                        <Typography variant={section === "all" ? "subtitle2" : "subtitle1"} fontWeight={700}
                                    sx={{mb: 1}}>
                            {t("hr.jobs.details.assignedBranches")}
                        </Typography>
                        <JobAssignmentList {...assignmentProps}/>
                    </Box>

                    <Box sx={{display: "flex", gap: 1}}>
                        <Select
                            size="small"
                            displayEmpty
                            value={assignBranch}
                            onChange={(e) => setAssignBranch(e.target.value)}
                            sx={{flex: 1, minWidth: 0}}
                            renderValue={(v) => v
                                ? branches.find((b) => b.id === v)?.name ?? v
                                : <Typography component="span" color="text.secondary">{t("hr.jobs.branchFilter.label")}</Typography>}
                        >
                            {branches.map((b) => (
                                <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                            ))}
                        </Select>
                        <Button variant="outlined" startIcon={<AddIcon/>} disabled={!assignBranch}
                                onClick={() => onAssign(assignBranch)}>
                            {t("hr.jobs.details.assign")}
                        </Button>
                    </Box>
                </>
            )}

            {showDetails && (onApprove || onReject || onClose) && (
                <Box sx={{display: "flex", gap: 1}}>
                    {onApprove && <Button size="small" color="success" onClick={() => onApprove(position.id)}>{t("common.actions.approve")}</Button>}
                    {onReject && <Button size="small" color="error" onClick={() => onReject(position.id)}>{t("common.actions.reject")}</Button>}
                    {onClose && <Button size="small" sx={{color: "#e91e63"}} onClick={() => onClose(position.id)}>{t("common.actions.close")}</Button>}
                </Box>
            )}

            {showDetails && (
                <Button variant="outlined" fullWidth startIcon={<EditOutlinedIcon/>} onClick={() => onEdit(position)}>
                    {t("hr.jobs.details.edit")}
                </Button>
            )}
        </Box>
    );
}
